import { markErrorAsExpected } from "@/services/expected-error";
import { queryClient } from "@/services/query-client";
import { recordError } from "@/services/record-error";
import { recordExpectedApiOutcome } from "@/services/record-expected-api-outcome";
import { recordSessionExpired } from "@/services/record-expected-outcome";
import {
  emitSessionExpired,
  shouldIgnoreSessionExpired,
} from "@/services/session-events";
import { useStore } from "@/services/store";

import delay from "delay";
import "isomorphic-fetch";

import { apiURL } from "../config";

const REQUEST_TIMEOUT = 15000;
const SESSION_SIGNIN_PATHS = [
  "/customer/signin_token",
  "/therapist/signin_token",
  "/admin/signin_token",
];
const USER_GOOGLE_ERROR_CODES = [
  "GOOGLE_NOT_CONNECTED",
  "GOOGLE_MISSING_REQUIRED_SCOPE",
  "GOOGLE_TOKEN_INVALID",
  "GOOGLE_PERMISSION_DENIED",
  "GOOGLE_CALENDAR_NOT_FOUND",
];
const expiredSessionAccounts = new WeakSet();

function shouldRetryPostError(error) {
  if (["NETWORK_ERROR", "REQUEST_TIMEOUT"].includes(error?.code)) return true;
  return [502, 503].includes(Number(error?.status));
}

function dispatchSessionExpired(path, error) {
  if (error?.status !== 401) return;
  if (error?.code !== "SESSION_EXPIRED") return;
  if (shouldIgnoreSessionExpired(path)) return;

  const activeRole = useStore.getState().activeRole;
  const endpointRole = String(path || "").match(
    /^\/(admin|customer|therapist)(?:\/|$)/
  )?.[1];
  const role = endpointRole || activeRole;
  if (role !== activeRole) return;
  const account = queryClient.getQueryData([`/${role}/signin_token`])?.user;
  if (!role || !account?._id) return;
  if (expiredSessionAccounts.has(account)) return;

  expiredSessionAccounts.add(account);
  recordSessionExpired({ accountRole: role });

  emitSessionExpired({
    role,
    path,
    code: error?.code,
  });
}

function getOnlineStatus() {
  return typeof navigator !== "undefined" ? navigator.onLine : undefined;
}

function normalizeApiError(error) {
  return {
    code: error?.code || "API_ERROR",
    message: error?.message || "",
    name: error?.name || "",
    status: error?.status,
    statusText: error?.statusText || error?.status_text || "",
  };
}

function isSessionSigninPath(path) {
  return SESSION_SIGNIN_PATHS.includes(String(path || ""));
}

function normalizeEndpoint(path) {
  const endpoint = String(path || "").split("?")[0];
  if (endpoint.startsWith("/customer/forgot_password_reset/"))
    return "/customer/forgot_password_reset/:id";

  return endpoint
    .split("/")
    .map((part) => {
      if (/^[a-f\d]{24}$/i.test(part)) return ":id";
      if (/^[\w-]*\d[\w-]*$/.test(part)) return ":id";
      return part;
    })
    .join("/");
}

function isExpectedApiError({ method, path, error, code }) {
  const normalizedError = normalizeApiError(error);
  if (code) normalizedError.code = code;
  const status = Number(normalizedError.status);
  const endpoint = normalizeEndpoint(path);

  if (
    isSessionSigninPath(path) &&
    ["REQUEST_TIMEOUT", "NETWORK_ERROR"].includes(normalizedError.code)
  )
    return true;
  if (normalizedError.code === "REQUEST_TIMEOUT") return true;
  if (
    method === "POST" &&
    endpoint === "/public-therapist/:id/availability/search" &&
    normalizedError.code === "NETWORK_ERROR"
  )
    return true;
  if (normalizedError.code === "NETWORK_ERROR") return false;
  // Panne de la recherche d'adresse officielle : l'API la signale déjà une
  // fois à Sentry ; chaque recherche de la fenêtre ne doit pas la redoubler.
  if (
    method === "POST" &&
    status === 503 &&
    normalizedError.code === "ADDRESS_SEARCH_UNAVAILABLE" &&
    (endpoint === "/therapist/customers/address/suggest" ||
      /^\/therapist\/customers\/[^/]+\/modification-requests$/.test(endpoint))
  )
    return true;
  if (Number.isFinite(status) && status >= 500) return false;

  if (
    method === "POST" &&
    status >= 400 &&
    status < 500 &&
    ["/customer/signup", "/therapist/signup"].includes(endpoint)
  ) {
    return [
      "EMAIL_AND_PASSWORD_REQUIRED",
      "EMAIL_NOT_VALID",
      "INVALID_BIRTH_DATE",
      "INVALID_BODY",
      "MISSING_PARAMS",
      "PASSWORD_NOT_VALIDATED",
      "PHONE_NOT_VALID",
      "PHONE_NOT_VERIFIED",
      "PHONE_VERIFICATION_EXPIRED",
      "REFERRAL_ALREADY_CLAIMED",
      "REFERRAL_CODE_INVALID",
      "REFERRAL_LINK_REQUIRED",
      "REFERRAL_REFERRED_INELIGIBLE",
      "REFERRAL_REFERRER_INELIGIBLE",
      "REFERRAL_SELF_FORBIDDEN",
      "REFERRAL_SLOTS_FULL",
      "SIGNUP_REPLAY_CONFLICT",
      "TERMS_NOT_ACCEPTED",
      "USER_ALREADY_REGISTERED",
    ].includes(normalizedError.code);
  }

  if (
    method === "POST" &&
    [400, 401, 403].includes(status) &&
    ["/admin/signin", "/customer/signin", "/therapist/signin"].includes(
      endpoint
    ) &&
    [
      "ACCOUNT_PENDING",
      "CUSTOMER_ACCOUNT_UNCLAIMED",
      "EMAIL_OR_PASSWORD_INVALID",
      "USER_NOT_EXISTS",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    [400, 429].includes(status) &&
    (/^\/(customer|therapist)\/signup\/sms\/(send|resend|verify)$/.test(
      endpoint
    )
      ? [
          "PHONE_NOT_VERIFIED",
          "PHONE_VERIFICATION_EXPIRED",
          "SMS_OTP_EXPIRED",
          "SMS_OTP_INVALID",
          "SMS_OTP_RATE_LIMITED",
          "SMS_OTP_RESEND_TOO_SOON",
          "SMS_OTP_TOO_MANY_ATTEMPTS",
        ].includes(normalizedError.code)
      : ["/customer/signup", "/therapist/signup"].includes(endpoint) &&
        ["PHONE_NOT_VERIFIED", "PHONE_VERIFICATION_EXPIRED"].includes(
          normalizedError.code
        ))
  )
    return true;
  if (
    method === "POST" &&
    [400, 409].includes(status) &&
    ["/booking/checkout", "/booking/customer"].includes(endpoint) &&
    [
      "BOOKING_TOO_SOON",
      "OFFICE_NOT_AVAILABLE",
      "SLOT_ALREADY_BOOKED",
      "SLOT_NOT_ALIGNED",
      "SLOT_OFFER_NOT_AVAILABLE",
      "SLOT_OUTSIDE_OPENING_HOURS",
      "SLOT_OVERLAP",
      "SLOT_OVERLAPS_ABSENCE",
      "SLOT_TOO_SOON",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    status === 409 &&
    endpoint === "/booking/:id/reschedule" &&
    ["SLOT_ALREADY_BOOKED", "SLOT_OVERLAP"].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    status === 409 &&
    endpoint === "/booking/customer/:id/reschedule" &&
    [
      "BOOKING_TOO_SOON",
      "OFFICE_NOT_AVAILABLE",
      "SLOT_ALREADY_BOOKED",
      "SLOT_NOT_ALIGNED",
      "SLOT_OFFER_NOT_AVAILABLE",
      "SLOT_OUTSIDE_OPENING_HOURS",
      "SLOT_OVERLAP",
      "SLOT_OVERLAPS_ABSENCE",
      "SLOT_SPANS_MULTIPLE_DAYS",
    ].includes(normalizedError.code)
  )
    return true;
  if (status === 401 && isSessionSigninPath(path)) return true;
  if (
    status === 401 &&
    normalizedError.code === "SESSION_EXPIRED" &&
    endpoint &&
    !shouldIgnoreSessionExpired(path)
  )
    return true;
  if (
    method === "POST" &&
    status === 400 &&
    endpoint === "/referral/invite" &&
    [
      "EMAIL_NOT_VALID",
      "REFERRAL_REFERRER_INELIGIBLE",
      "REFERRAL_SLOTS_FULL",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    method === "PUT" &&
    [400, 409].includes(status) &&
    endpoint === "/admin/customer/:id/phone" &&
    ["PHONE_NOT_VALID", "PHONE_ALREADY_USED"].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    status === 400 &&
    endpoint === "/admin/customer/:id/resend-claim-link" &&
    ["MISSING_PARAMS", "INVALID_STATUS"].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    [401, 404].includes(status) &&
    endpoint === "/admin/customer/:id/login_as" &&
    [
      "CUSTOMER_ACCOUNT_UNCLAIMED",
      "INVALID_STATUS",
      "USER_NOT_EXISTS",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    [400, 409].includes(status) &&
    endpoint === "/admin/customer-modification-requests/:id/accept" &&
    [
      "EMAIL_NOT_VALID",
      "PHONE_NOT_VALID",
      "PHONE_ALREADY_USED",
      "USER_ALREADY_REGISTERED",
      "INVALID_STATUS",
      "INVALID_BODY",
      "MISSING_PARAMS",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    status === 400 &&
    endpoint === "/admin/customer-modification-requests/:id/refuse" &&
    ["INVALID_STATUS"].includes(normalizedError.code)
  )
    return true;
  if (
    method === "POST" &&
    [400, 409].includes(status) &&
    endpoint === "/therapist/customers/:id/modification-requests" &&
    [
      "MISSING_PARAMS",
      "INVALID_BODY",
      "EMAIL_NOT_VALID",
      "PHONE_NOT_VALID",
      "INVALID_STATUS",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    ["GET", "POST"].includes(method) &&
    status === 400 &&
    (endpoint === "/customer/forgot_password_reset/:id" ||
      endpoint === "/customer/forgot_password_reset") &&
    [
      "PASSWORD_TOKEN_EXPIRED_OR_INVALID",
      "PASSWORD_NOT_VALIDATED",
      "TERMS_NOT_ACCEPTED",
    ].includes(normalizedError.code)
  )
    return true;
  if (
    ["POST", "PUT", "DELETE"].includes(method) &&
    [400, 403, 404].includes(status) &&
    (endpoint === "/absence" || endpoint === "/absence/:id") &&
    USER_GOOGLE_ERROR_CODES.includes(normalizedError.code)
  )
    return true;
  if (status === 404 && endpoint === "/invoice/:id") return true;
  if (
    method === "GET" &&
    status === 404 &&
    endpoint === "/booking/customer/:id"
  )
    return true;
  if (status === 404 && endpoint.startsWith("/public-therapist/slug/"))
    return true;
  if (Number.isFinite(status) && status >= 400) return false;

  return false;
}

function captureApiClientError({
  bookingMode,
  method,
  path,
  error,
  code: errorCode,
}) {
  const normalizedError = normalizeApiError(error);
  if (errorCode) normalizedError.code = errorCode;
  const endpoint = normalizeEndpoint(path);
  const status = normalizedError.status ? String(normalizedError.status) : "";
  const code = normalizedError.code || "API_ERROR";
  const expected = isExpectedApiError({
    method,
    path,
    error,
    code: errorCode,
  });

  if (expected) markErrorAsExpected(error);
  if (!expected && error && ["function", "object"].includes(typeof error)) {
    try {
      error.expected = false;
    } catch {
      // Classification still reaches recordError for non-extensible errors.
    }
  }

  if (expected)
    recordExpectedApiOutcome({
      bookingMode,
      code,
      endpoint,
      method,
    });

  recordError(error, {
    source: "api-client",
    operation: `${method} ${endpoint}`,
    expected,
    attributes: {
      api_url: apiURL,
      browser_error: error?.browser_error,
      code,
      endpoint,
      method,
      name: normalizedError.name,
      online: getOnlineStatus(),
      response_status_text: normalizedError.statusText,
      status,
    },
    // A connection drop fails every request the page sends at once: one
    // issue for all of them instead of one per endpoint.
    groupingKey:
      code === "NETWORK_ERROR"
        ? ["api-client", "NETWORK_ERROR"]
        : ["api-client", method, endpoint, status || "unknown", code],
  });
}

async function parseJsonResponse(response) {
  if (response.status === 204) return { ok: true };

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    const message = await response.text();
    return { code: "API_ERROR", message };
  }

  const data = await response.json();
  if (data?.ok === false && !data.code) return { ...data, code: "API_ERROR" };
  return data;
}

function captureTransportError({ code, method, path, error }) {
  captureApiClientError({
    code,
    method,
    path,
    error,
  });
}

function captureApiError({ bookingMode, method, path, error }) {
  captureApiClientError({ bookingMode, method, path, error });
}

class api {
  setToken() {}

  getToken() {
    return "";
  }

  async get(path, options = {}) {
    const attemptCount = options.retryTransientOnce ? 2 : 1;

    for (let attempt = 0; attempt < attemptCount; attempt += 1) {
      const controller = new AbortController();
      const timeout = setTimeout(
        () => controller.abort(),
        options.timeout ?? REQUEST_TIMEOUT
      );

      try {
        const response = await fetch(`${apiURL}${path}`, {
          mode: "cors",
          method: "GET",
          credentials: "include",
          signal: controller.signal,
        });

        const json = await parseJsonResponse(response);

        if (!response.ok || (options.rejectFailure && json?.ok === false)) {
          const apiError = {
            ...json,
            status: response.status,
            statusText: response.statusText,
          };
          throw apiError;
        }

        return json;
      } catch (error) {
        let normalizedError = error;
        if (error?.name === "AbortError") {
          normalizedError = {
            ok: false,
            code: "REQUEST_TIMEOUT",
            message:
              "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
            name: error.name,
            stack: error.stack,
          };
        }
        if (error instanceof TypeError) {
          normalizedError = {
            ok: false,
            code: "NETWORK_ERROR",
            message: "Impossible de contacter le serveur.",
            browser_error: error.message,
            name: error.name,
            stack: error.stack,
          };
        }

        if (
          attempt === 0 &&
          options.retryTransientOnce &&
          shouldRetryPostError(normalizedError)
        ) {
          if (
            options.capture !== false ||
            ["NETWORK_ERROR", "REQUEST_TIMEOUT"].includes(normalizedError.code)
          )
            captureApiError({ method: "GET", path, error: normalizedError });
          clearTimeout(timeout);
          await delay(150 + Math.floor(Math.random() * 151));
          continue;
        }

        dispatchSessionExpired(path, normalizedError);
        if (
          options.capture !== false ||
          ["NETWORK_ERROR", "REQUEST_TIMEOUT"].includes(normalizedError.code)
        )
          captureApiError({
            method: "GET",
            path,
            error: normalizedError,
          });
        throw normalizedError;
      } finally {
        clearTimeout(timeout);
      }
    }
  }

  async put(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${apiURL}${path}`, {
        mode: "cors",
        method: "PUT",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: typeof body === "string" ? body : JSON.stringify(body),
      });

      const json = await parseJsonResponse(response);

      if (!response.ok) {
        const apiError = {
          ...json,
          status: response.status,
          statusText: response.statusText,
        };
        throw apiError;
      }

      return json;
    } catch (error) {
      if (error?.name === "AbortError") {
        const normalizedError = {
          ok: false,
          code: "REQUEST_TIMEOUT",
          message:
            "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "REQUEST_TIMEOUT",
          method: "PUT",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      if (error instanceof TypeError) {
        const normalizedError = {
          ok: false,
          code: "NETWORK_ERROR",
          message: "Impossible de contacter le serveur.",
          browser_error: error.message,
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "NETWORK_ERROR",
          method: "PUT",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      dispatchSessionExpired(path, error);
      captureApiError({ method: "PUT", path, error });
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async postFormData(path, file) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    const formData = new FormData();
    formData.append(file.name, file, file.name);

    try {
      const response = await fetch(`${apiURL}${path}`, {
        mode: "cors",
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        body: formData,
      });

      const json = await parseJsonResponse(response);

      if (!response.ok) {
        const apiError = {
          ...json,
          status: response.status,
          statusText: response.statusText,
        };
        throw apiError;
      }

      return json;
    } catch (error) {
      if (error?.name === "AbortError") {
        const normalizedError = {
          ok: false,
          code: "REQUEST_TIMEOUT",
          message:
            "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "REQUEST_TIMEOUT",
          method: "POST_FORM_DATA",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      if (error instanceof TypeError) {
        const normalizedError = {
          ok: false,
          code: "NETWORK_ERROR",
          message: "Impossible de contacter le serveur.",
          browser_error: error.message,
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "NETWORK_ERROR",
          method: "POST_FORM_DATA",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      dispatchSessionExpired(path, error);
      captureApiError({ method: "POST_FORM_DATA", path, error });
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async remove(path) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${apiURL}${path}`, {
        mode: "cors",
        credentials: "include",
        method: "DELETE",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const json = await parseJsonResponse(response);

      if (!response.ok) {
        const apiError = {
          ...json,
          status: response.status,
          statusText: response.statusText,
        };
        throw apiError;
      }

      return json;
    } catch (error) {
      if (error?.name === "AbortError") {
        const normalizedError = {
          ok: false,
          code: "REQUEST_TIMEOUT",
          message:
            "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "REQUEST_TIMEOUT",
          method: "DELETE",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      if (error instanceof TypeError) {
        const normalizedError = {
          ok: false,
          code: "NETWORK_ERROR",
          message: "Impossible de contacter le serveur.",
          browser_error: error.message,
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "NETWORK_ERROR",
          method: "DELETE",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      dispatchSessionExpired(path, error);
      captureApiError({ method: "DELETE", path, error });
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async post(
    path,
    body,
    {
      bookingMode,
      timeout: timeoutMs = REQUEST_TIMEOUT,
      retryTransientOnce = false,
      rejectFailure = false,
    } = {}
  ) {
    const serializedBody =
      typeof body === "string" ? body : JSON.stringify(body);
    const attemptCount = retryTransientOnce ? 2 : 1;

    for (let attempt = 0; attempt < attemptCount; attempt += 1) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(`${apiURL}${path}`, {
          mode: "cors",
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: {
            "Content-Type": "application/json",
          },
          body: serializedBody,
        });

        const json = await parseJsonResponse(response);

        if (response.status !== 200 || (rejectFailure && json?.ok === false)) {
          const apiError = {
            ...json,
            status: response.status,
            statusText: response.statusText,
          };
          throw apiError;
        }

        return json;
      } catch (error) {
        let normalizedError = error;
        if (error?.name === "AbortError") {
          normalizedError = {
            ok: false,
            code: "REQUEST_TIMEOUT",
            message:
              "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
            name: error.name,
            stack: error.stack,
          };
        }
        if (error instanceof TypeError) {
          normalizedError = {
            ok: false,
            code: "NETWORK_ERROR",
            message: "Impossible de contacter le serveur.",
            browser_error: error.message,
            name: error.name,
            stack: error.stack,
          };
        }

        if (
          attempt === 0 &&
          retryTransientOnce &&
          shouldRetryPostError(normalizedError)
        ) {
          captureApiError({ method: "POST", path, error: normalizedError });
          clearTimeout(timeout);
          await delay(150 + Math.floor(Math.random() * 151));
          continue;
        }

        dispatchSessionExpired(path, normalizedError);
        captureApiError({
          bookingMode,
          method: "POST",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      } finally {
        clearTimeout(timeout);
      }
    }
  }

  async download(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${apiURL}${path}`, {
        mode: "cors",
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
        body: typeof body === "string" ? body : JSON.stringify(body),
      });

      if (response.status !== 200) {
        throw response;
      }

      return response;
    } catch (error) {
      if (error?.name === "AbortError") {
        const normalizedError = {
          ok: false,
          code: "REQUEST_TIMEOUT",
          message:
            "Cette action demande un peu plus de temps. Vous pourrez réessayer dans quelques instants.",
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "REQUEST_TIMEOUT",
          method: "DOWNLOAD_POST",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      if (error instanceof TypeError) {
        const normalizedError = {
          ok: false,
          code: "NETWORK_ERROR",
          message: "Impossible de contacter le serveur.",
          browser_error: error.message,
          name: error.name,
          stack: error.stack,
        };
        captureTransportError({
          code: "NETWORK_ERROR",
          method: "DOWNLOAD_POST",
          path,
          error: normalizedError,
        });
        throw normalizedError;
      }

      const apiError = normalizeApiError(error);
      dispatchSessionExpired(path, apiError);
      captureApiError({ method: "DOWNLOAD_POST", path, error });
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }
}

const API = new api();
export default API;
